import { useMemo } from "react";
import { useUserPreferences } from "~/hooks/useUserPreferences";

export type SortDirection = "asc" | "desc";

export type SortKind = "alpha" | "date" | "numeric";

export interface SortOption<T> {
  key: string;
  label: string;
  kind: SortKind;
  accessor: (item: T) => string | number;
  defaultDirection: SortDirection;
}

export interface PersistedSort {
  key: string;
  direction: SortDirection;
}

export interface UseSortResult<T> {
  sortKey: string;
  direction: SortDirection;
  activeOption: SortOption<T>;
  setSortKey: (key: string) => void;
  toggleDirection: () => void;
  sorted: T[];
}

function compare(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

/** Sorts `items` by a chosen field and direction, persisted to localStorage. */
export function useSort<T>(
  storageKey: string,
  options: SortOption<T>[],
  items: T[]
): UseSortResult<T> {
  const fallback: PersistedSort = {
    key: options[0].key,
    direction: options[0].defaultDirection,
  };
  const [preferences, updatePreferences] = useUserPreferences();
  const stored = preferences.listSortPreferences?.[storageKey];
  const state = stored && options.some((o) => o.key === stored.key) ? stored : fallback;

  const persist = (next: PersistedSort) =>
    updatePreferences({
      listSortPreferences: { ...preferences.listSortPreferences, [storageKey]: next },
    });

  const activeOption = options.find((o) => o.key === state.key) ?? options[0];

  const setSortKey = (key: string) => {
    const option = options.find((o) => o.key === key);
    if (!option) {
      return;
    }
    persist({ key, direction: option.defaultDirection });
  };

  const toggleDirection = () => {
    persist({ key: state.key, direction: state.direction === "asc" ? "desc" : "asc" });
  };

  const sorted = useMemo(() => {
    const factor = state.direction === "asc" ? 1 : -1;
    return [...items].sort(
      (a, b) => factor * compare(activeOption.accessor(a), activeOption.accessor(b))
    );
  }, [items, activeOption, state.direction]);

  return {
    sortKey: state.key,
    direction: state.direction,
    activeOption,
    setSortKey,
    toggleDirection,
    sorted,
  };
}
